import { z } from "zod";

import { SignalFilterStatus } from "@/enum/list.enum";

import { TrackerSignalPriority } from "../../../schema/trackerSignalResult";

import { TrackerSignalResultAdditionalSchema } from "../../../../Apollo/schema/get-people";

export type TrackerSignalResultAdditional = z.output<typeof TrackerSignalResultAdditionalSchema>;

export interface ProspectSignalGroup {
	inboxIndex: number;
	listPersonId: number;
	prospectId?: number;
	prospectName: string;
	organizationName: string;
	signals: TrackerSignalResultAdditional[];
}

export type SignalFilterMode =
	| { kind: "all" }
	| { kind: "any-with-signal" }
	| { kind: "specific"; priorities: Set<string> };

const SIGNAL_FILTER_PRIORITY_MAP: Record<
	Exclude<SignalFilterStatus, SignalFilterStatus.ALL | SignalFilterStatus.ALL_WITH_SIGNAL>,
	string
> = {
	[SignalFilterStatus.S1]: TrackerSignalPriority.PRIORITY_1,
	[SignalFilterStatus.S2]: TrackerSignalPriority.PRIORITY_2,
	[SignalFilterStatus.S3]: TrackerSignalPriority.PRIORITY_3,
	[SignalFilterStatus.DQ]: TrackerSignalPriority.DISQUALIFIED,
};

export const getSignalTimestamp = (signal: TrackerSignalResultAdditional): number => {
	const publishedAt = signal.publishedDate ? new Date(signal.publishedDate).getTime() : Number.NaN;

	if (Number.isFinite(publishedAt)) {
		return publishedAt;
	}

	const createdAt = signal.createdAt ? new Date(signal.createdAt).getTime() : Number.NaN;

	return Number.isFinite(createdAt) ? createdAt : 0;
};

export const getCombinedTrackerSignals = (
	firstSignal: TrackerSignalResultAdditional[] | null | undefined,
	organizationSignals: TrackerSignalResultAdditional[] | null | undefined,
	prospectSignals: TrackerSignalResultAdditional[] | null | undefined
): TrackerSignalResultAdditional[] => {
	const signalsById = new Map<number, TrackerSignalResultAdditional>();
	const primarySignalId = firstSignal?.[0]?.id;

	[...(firstSignal ?? []), ...(organizationSignals ?? []), ...(prospectSignals ?? [])].forEach(
		(signal) => {
			const existingSignal = signalsById.get(signal.id);

			if (!existingSignal || getSignalTimestamp(signal) > getSignalTimestamp(existingSignal)) {
				signalsById.set(signal.id, signal);
			}
		}
	);

	const sortedSignals = Array.from(signalsById.values()).sort(
		(a, b) => getSignalTimestamp(b) - getSignalTimestamp(a)
	);

	if (!primarySignalId) {
		return sortedSignals;
	}

	const primarySignal = signalsById.get(primarySignalId);

	if (!primarySignal) {
		return sortedSignals;
	}

	return [primarySignal, ...sortedSignals.filter((signal) => signal.id !== primarySignalId)];
};

export const prospectHasFeedSignal = (listPerson: {
	trackerSignalResult?: TrackerSignalResultAdditional | null;
	prospect?: {
		trackerSignalResults?: TrackerSignalResultAdditional[] | null;
		organization?: { trackerSignalResults?: TrackerSignalResultAdditional[] | null } | null;
	} | null;
}): boolean =>
	getCombinedTrackerSignals(
		listPerson.trackerSignalResult ? [listPerson.trackerSignalResult] : [],
		listPerson.prospect?.organization?.trackerSignalResults,
		listPerson.prospect?.trackerSignalResults
	).length > 0;

export const resolveSignalFilterMode = (
	signalFilterStatus: SignalFilterStatus[] | undefined
): SignalFilterMode => {
	if (!signalFilterStatus || signalFilterStatus.length === 0) {
		return { kind: "all" };
	}

	if (signalFilterStatus.includes(SignalFilterStatus.ALL)) {
		return { kind: "all" };
	}

	if (signalFilterStatus.includes(SignalFilterStatus.ALL_WITH_SIGNAL)) {
		return { kind: "any-with-signal" };
	}

	const priorities = new Set<string>();

	signalFilterStatus.forEach((status) => {
		const mapped =
			SIGNAL_FILTER_PRIORITY_MAP[
				status as Exclude<
					SignalFilterStatus,
					SignalFilterStatus.ALL | SignalFilterStatus.ALL_WITH_SIGNAL
				>
			];

		if (mapped) {
			priorities.add(mapped);
		}
	});

	if (priorities.size === 0) {
		return { kind: "all" };
	}

	return { kind: "specific", priorities };
};

export const signalMatchesFilter = (
	signal: TrackerSignalResultAdditional,
	mode: SignalFilterMode
): boolean => {
	if (mode.kind === "all") {
		return true;
	}

	if (mode.kind === "any-with-signal") {
		return !!signal.priority;
	}

	return !!signal.priority && mode.priorities.has(signal.priority);
};
